import { CircleLoader } from "@/components/shared/loaders";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DecisionActionEnum, DecisionEnum } from "@/graphql/__generated__/graphql";
import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  userDecision: DecisionEnum;
  loading?: boolean;
  userAction: DecisionActionEnum;
  isSkip?: boolean;
  awaitingOrganizationName?: string;
}

/**
 * DecisionConfirmationAlertDialog component renders an alert dialog to confirm the user's decision.
 *
 * @param {Props} props - The properties passed to the component.
 * @param {boolean} props.open - Determines if the alert dialog is open.
 * @param {function} props.onOpenChange - Callback function to handle the change in the open state of the dialog.
 * @param {DecisionActionEnum} props.userDecision - The decision action taken by the user.
 * @param {boolean} props.loading - Loading flag for the submitDecision mutation.
 *
 * @returns {JSX.Element} The rendered alert dialog component.
 */
const DecisionConfirmationAlertDialog = (props: Props): JSX.Element => {

  const { open, onOpenChange, userDecision, loading, userAction, isSkip, awaitingOrganizationName } = props;

  // Hooks
  const { t } = useTranslation(["common", "modals"]);

  /**
   * Returns a confirmation text based on the user's decision.
   *
   * @param {DecisionActionEnum} userDecision - The decision action made by the user.
   * @returns {string} The confirmation text corresponding to the user's decision.
   *
   * @remarks
   * - If the user decision is `Approve`, the function returns a message indicating that the user is about to agree with the decision and that the action cannot be undone.
   * - If the user decision is `Flip`, the function returns a message indicating that the user is about to flip the decision and that the action cannot be undone.
   */
  const getDecisionText = (userDecision: DecisionEnum, userAction: DecisionActionEnum): string => {
    let prefix = "";
    if (isSkip && userAction === DecisionActionEnum.Approve) {
      prefix = t("modals:biddecision.skipagreetext", { organizationName: awaitingOrganizationName });
    }
    else if (isSkip && userAction === DecisionActionEnum.Flip) {
      prefix = t("modals:biddecision.skipdisagreetext", { organizationName: awaitingOrganizationName });
    }
    else if (isSkip && userDecision === DecisionEnum.Bid) {
      prefix = t("modals:biddecision.skipbidtext", { organizationName: awaitingOrganizationName });
    }
    else if (isSkip && userDecision === DecisionEnum.Nobid) {
      prefix = t("modals:biddecision.skipnobidtext", { organizationName: awaitingOrganizationName });
    }
    else if(userAction === DecisionActionEnum.Approve) {
      prefix = t("modals:biddecision.agreetext");
    }
    else if(userAction === DecisionActionEnum.Flip) {
      prefix = t("modals:biddecision.disagreetext");
    }
    else if(userDecision === DecisionEnum.Bid) {
      prefix = t("modals:biddecision.bidtext");
    }
    else if(userDecision === DecisionEnum.Nobid) {
      prefix = t("modals:biddecision.nobidtext");
    }
    return `${prefix} ${t("modals:biddecision.disclaimer")}`;
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="select-none">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("modals:biddecision.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {getDecisionText(userDecision, userAction)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus={false} onKeyDown={(e) => e.preventDefault()}>
            {t("buttons.cancel")}
          </AlertDialogCancel>
          <Button
            autoFocus={false}
            className={cn("gap-2", loading && "cursor-wait")}
            type={loading ? "button" : "submit"}
            form="tender-decision-form"
            disabled={loading}
            onKeyDown={(e) => e.preventDefault()}
          >
            <CircleLoader visible={loading} height={16} width={16} strokeWidth={2}/>
            {loading? (
              t("buttons.submitting")
            ) : (
              t("buttons.submit")
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DecisionConfirmationAlertDialog;
